// Form validation utilities for Savidna CRM
import { getToday } from './helpers';

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
export const validatePhone = (phone: string): boolean => {
  if (!phone) return false;
  const trimmed = phone.trim();
  // Solo se permiten dígitos y símbolos de teléfono comunes (cualquier país).
  if (!/^[+(]?[\d\s\-().]*$/.test(trimmed)) return false;
  // El '+' (si existe) solo puede ir al inicio.
  if (trimmed.indexOf('+') > 0) return false;
  // Contar dígitos reales: estándar internacional E.164 = 7 a 15 dígitos.
  const digits = trimmed.replace(/\D/g, '');
  return digits.length >= 7 && digits.length <= 15;
};
export const validateRequired = (value: string | number | null | undefined): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};
export const validateMinLength = (value: string, minLength: number): boolean => {
  return value.length >= minLength;
};
export const validateMaxLength = (value: string, maxLength: number): boolean => {
  return value.length <= maxLength;
};
export const validatePositiveNumber = (value: number): boolean => {
  return value > 0;
};
export const validateAge = (age: number): boolean => {
  return age >= 0 && age <= 150;
};
export const validateTime = (time: string): boolean => {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
};
export const validateDate = (date: string): boolean => {
  const dateObj = new Date(date);
  return !isNaN(dateObj.getTime());
};
export const validateFutureDate = (date: string): boolean => {
  const [year, month, day] = date.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dateObj >= today;
};

// Dominican Cédula Validation
export const validateDominicanCedula = (cedula: string): boolean => {
  // Remove dashes and spaces
  const cleanCedula = cedula.replace(/[\s-]/g, '');

  // Must be 11 digits
  if (!/^\d{11}$/.test(cleanCedula)) {
    return false;
  }

  // Luhn Algorithm implementation for Dominican Cédula
  let sum = 0;
  const weights = [1, 2, 1, 2, 1, 2, 1, 2, 1, 2];
  for (let i = 0; i < 10; i++) {
    let digit = parseInt(cleanCedula[i]) * weights[i];
    if (digit >= 10) {
      const digitStr = digit.toString();
      digit = parseInt(digitStr[0]) + parseInt(digitStr[1]);
    }
    sum += digit;
  }
  const verifier = (10 - sum % 10) % 10;
  return verifier === parseInt(cleanCedula[10]);
};
export const validatePassword = (password: string): {
  isValid: boolean;
  message: string;
} => {
  if (password.length < 8) {
    return {
      isValid: false,
      message: 'La contraseña debe tener al menos 8 caracteres'
    };
  }
  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos una letra mayúscula'
    };
  }
  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos una letra minúscula'
    };
  }
  if (!/[0-9]/.test(password)) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos un número'
    };
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos un carácter especial (!@#$%...)'
    };
  }
  return {
    isValid: true,
    message: ''
  };
};

// Password strength indicator
export const getPasswordStrength = (password: string): {
  level: 'weak' | 'medium' | 'strong' | 'very_strong';
  score: number;
  label: string;
} => {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;
  if (password.length >= 16) score++;
  if (score <= 2) return {
    level: 'weak',
    score,
    label: 'Débil'
  };
  if (score <= 4) return {
    level: 'medium',
    score,
    label: 'Media'
  };
  if (score <= 5) return {
    level: 'strong',
    score,
    label: 'Fuerte'
  };
  return {
    level: 'very_strong',
    score,
    label: 'Muy fuerte'
  };
};
export const validateLoginForm = (email: string, password: string): ValidationResult => {
  const errors: Record<string, string> = {};
  if (!validateRequired(email)) {
    errors.email = 'El correo electrónico es requerido';
  } else if (!validateEmail(email)) {
    errors.email = 'El correo electrónico no es válido';
  }
  if (!validateRequired(password)) {
    errors.password = 'La contraseña es requerida';
  }
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
export const validateRegisterForm = (name: string, email: string, password: string, confirmPassword: string): ValidationResult => {
  const errors: Record<string, string> = {};
  if (!validateRequired(name)) {
    errors.name = 'El nombre es requerido';
  } else if (!validateMinLength(name, 2)) {
    errors.name = 'El nombre debe tener al menos 2 caracteres';
  } else if (!validateMaxLength(name, 100)) {
    errors.name = 'El nombre no puede exceder 100 caracteres';
  }
  if (!validateRequired(email)) {
    errors.email = 'El correo electrónico es requerido';
  } else if (!validateEmail(email)) {
    errors.email = 'El correo electrónico no es válido';
  }
  const passwordValidation = validatePassword(password);
  if (!validateRequired(password)) {
    errors.password = 'La contraseña es requerida';
  } else if (!passwordValidation.isValid) {
    errors.password = passwordValidation.message;
  }
  if (password !== confirmPassword) {
    errors.confirmPassword = 'Las contraseñas no coinciden';
  }
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
export const validatePatientForm = (data: {
  name: string;
  cedula?: string;
  birthDate?: string;
  age?: number;
  gender: string;
  phone: string;
  email: string;
  address: string;
  emergencyContactPhone?: string;
}): ValidationResult => {
  const errors: Record<string, string> = {};
  if (!validateRequired(data.name)) {
    errors.name = 'El nombre es requerido';
  } else if (!validateMaxLength(data.name, 200)) {
    errors.name = 'El nombre no puede exceder 200 caracteres';
  }

  // Validate Cédula if provided
  if (data.cedula && !validateDominicanCedula(data.cedula)) {
    errors.cedula = 'La cédula no es válida (formato: 000-0000000-0)';
  }

  // Validate Birth Date only if provided
  if (data.birthDate && !validateDate(data.birthDate)) {
    errors.birthDate = 'La fecha de nacimiento no es válida';
  }

  // Validate age only if provided
  if (data.age !== undefined && data.age !== null && !validateAge(data.age)) {
    errors.age = 'La edad debe estar entre 0 y 150';
  }
  if (!validateRequired(data.gender)) {
    errors.gender = 'El sexo es requerido';
  }
  // Teléfono opcional, pero si lo escriben debe ser válido
  if (validateRequired(data.phone) && !validatePhone(data.phone)) {
    errors.phone = 'El teléfono no es válido (7 a 15 dígitos)';
  }
  // Teléfono de emergencia opcional, validado solo si se escribe
  if (validateRequired(data.emergencyContactPhone || '') && !validatePhone(data.emergencyContactPhone as string)) {
    errors.emergencyContactPhone = 'El teléfono de emergencia no es válido (7 a 15 dígitos)';
  }
  if (data.email && !validateEmail(data.email)) {
    errors.email = 'El correo electrónico no es válido';
  }
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Validación del formulario de doctores (teléfono opcional pero válido si se escribe)
export const validateDoctorForm = (data: {
  name: string;
  specialtyId: string;
  phone: string;
  email: string;
}): ValidationResult => {
  const errors: Record<string, string> = {};
  if (!validateRequired(data.name)) {
    errors.name = 'El nombre es requerido';
  } else if (!validateMaxLength(data.name, 200)) {
    errors.name = 'El nombre no puede exceder 200 caracteres';
  }
  if (!validateRequired(data.specialtyId)) {
    errors.specialtyId = 'La especialidad es requerida';
  }
  if (validateRequired(data.phone) && !validatePhone(data.phone)) {
    errors.phone = 'El teléfono no es válido (7 a 15 dígitos)';
  }
  if (!validateRequired(data.email)) {
    errors.email = 'El correo es requerido';
  } else if (!validateEmail(data.email)) {
    errors.email = 'El correo electrónico no es válido';
  }
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
export const validateAppointmentForm = (data: {
  patientId: string;
  doctorId: string;
  serviceId?: string;
  serviceIds?: string[];
  date: string;
  time: string;
}, options?: { allowPastDate?: boolean }): ValidationResult => {
  const errors: Record<string, string> = {};
  if (!validateRequired(data.patientId)) {
    errors.patientId = 'Debe seleccionar un paciente';
  }
  if (!validateRequired(data.doctorId)) {
    errors.doctorId = 'Debe seleccionar un doctor';
  }

  // Check if at least one service is selected (either single serviceId or array of serviceIds)
  const hasServiceId = validateRequired(data.serviceId);
  const hasServiceIds = data.serviceIds && data.serviceIds.length > 0;
  if (!hasServiceId && !hasServiceIds) {
    errors.serviceId = 'Debe seleccionar al menos un servicio';
  }
  if (!validateRequired(data.date)) {
    errors.date = 'La fecha es requerida';
  } else if (!validateDate(data.date)) {
    errors.date = 'La fecha no es válida';
  } else if (!options?.allowPastDate && data.date < getToday()) {
    errors.date = 'No se pueden crear citas con una fecha pasada';
  }
  if (!validateRequired(data.time)) {
    errors.time = 'La hora es requerida';
  } else if (!validateTime(data.time)) {
    errors.time = 'La hora no es válida';
  }
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};